import { useState, type FormEvent } from "react";
import {
  CloudDownload,
  KeyRound,
  PlugZap,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import type { Platform } from "../domain/types";
import { Button, Field, Modal, PageHeader, StatusChip } from "../components/UI";
import { PlatformBadge } from "../components/PlatformBadge";
import { dateTime } from "../lib/format";
import { useWeSell } from "../state/WeSellProvider";

function connectionCopy(slug: Platform["slug"]) {
  if (slug === "amazon")
    return {
      title: "Amazon Creators API",
      description:
        "Catálogo e dados de produto pela API oficial. Comissões entram pelo relatório do Associates.",
      fields: [
        { key: "credentialId", label: "Credential ID" },
        { key: "credentialSecret", label: "Credential Secret" },
        {
          key: "credentialVersion",
          label: "Credential Version",
          defaultValue: "3.1",
        },
        { key: "partnerTag", label: "Partner Tag" },
      ],
    };
  if (slug === "temu")
    return {
      title: "Temu Affiliate",
      description:
        "Relatórios baixados pelo conector local com sessão persistente. O login fica somente neste Mac.",
      fields: [{ key: "affiliateId", label: "Affiliate ID ou código" }],
    };
  return {
    title: "SHEIN Affiliate",
    description:
      "O conector local abre a central de afiliados e coleta relatórios e links oficiais. A sessão fica somente neste Mac.",
    fields: [{ key: "affiliateId", label: "Affiliate ID ou código" }],
  };
}

export function Connections() {
  const { data, saving, isDemo, connect, sync } = useWeSell();
  const [selected, setSelected] = useState<Platform | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");
  if (!data) return null;
  const open = (platform: Platform) => {
    setSelected(platform);
    setFields(
      Object.fromEntries(
        connectionCopy(platform.slug).fields
          .filter((field) => "defaultValue" in field)
          .map((field) => [field.key, field.defaultValue ?? ""]),
      ),
    );
    setMessage("");
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    try {
      await connect(selected.slug, selected.connectionMode, fields);
      setMessage(
        isDemo
          ? "Simulação concluída. No modo real, o segredo é criptografado no servidor."
          : selected.slug === "amazon"
            ? "Credencial validada pela Amazon e salva com segurança."
            : "Conexão salva com segurança.",
      );
    } catch {
      setMessage(
        selected.slug === "amazon"
          ? "A Amazon recusou a credencial. Confira ID, Secret, Version e Partner Tag."
          : "Não foi possível salvar a conexão.",
      );
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="Automação"
        title="Conexões"
        description="Depois da autorização inicial, o WeSell atualiza catálogo e relatórios sem pedir sua ajuda toda vez."
      />
      <aside className="security-banner">
        <ShieldCheck size={22} />
        <div>
          <strong>Autonomia sem guardar sua senha</strong>
          <p>
            Amazon usa credenciais próprias de API. Temu e SHEIN usam uma sessão
            local protegida; senha, cookies e 2FA nunca são enviados ao WeSell.
          </p>
        </div>
      </aside>
      <div className="connection-grid">
        {data.platforms.map((platform) => {
          const details = connectionCopy(platform.slug);
          return (
            <article className="connection-card panel" key={platform.id}>
              <header>
                <PlatformBadge platform={platform} />
                <StatusChip
                  status={
                    platform.connectionStatus === "connected"
                      ? "ok"
                      : platform.connectionStatus === "attention"
                        ? "error"
                        : "warning"
                  }
                  label={
                    platform.connectionStatus === "connected"
                      ? "Conectada"
                      : "Não conectada"
                  }
                />
              </header>
              <h2>{details.title}</h2>
              <p>{details.description}</p>
              <dl>
                <div>
                  <dt>Última sincronização</dt>
                  <dd>{dateTime(platform.lastSyncAt)}</dd>
                </div>
                <div>
                  <dt>Modo</dt>
                  <dd>
                    {platform.connectionMode === "official_api"
                      ? "API oficial"
                      : "Conector local"}
                  </dd>
                </div>
              </dl>
              <div className="connection-card__actions">
                <Button onClick={() => open(platform)}>
                  <PlugZap size={17} /> Configurar
                </Button>
                <Button
                  variant="secondary"
                  disabled={platform.connectionStatus !== "connected"}
                  onClick={() => sync(platform.slug)}
                >
                  <RefreshCw size={17} /> Sincronizar
                </Button>
              </div>
            </article>
          );
        })}
      </div>
      <section className="panel autonomy-panel">
        <span className="metric-card__icon">
          <CloudDownload size={22} />
        </span>
        <div>
          <h2>Como fica o automático</h2>
          <p>
            O conector roda neste Mac em horários definidos, baixa os relatórios
            e envia apenas linhas de venda ao banco. Se a loja encerrar a sessão
            ou pedir novo 2FA, o WeSell marca “precisa de atenção” e não tenta
            contornar a proteção da conta.
          </p>
        </div>
      </section>
      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? `Conectar ${selected.name}` : "Conectar"}
        description={selected ? connectionCopy(selected.slug).description : ""}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSelected(null)}>
              Fechar
            </Button>
            <Button form="connection-form" type="submit" loading={saving}>
              Salvar conexão
            </Button>
          </>
        }
      >
        {selected ? (
          <form id="connection-form" className="form-grid" onSubmit={submit}>
            <div className="credential-note">
              <KeyRound size={19} />
              <span>
                Dados sensíveis seguem direto para o cofre do servidor e nunca
                ficam no navegador ou no repositório.
              </span>
            </div>
            {connectionCopy(selected.slug).fields.map((field) => (
              <Field key={field.key} label={field.label}>
                {field.key === "credentialVersion" ? (
                  <select
                    value={fields[field.key] ?? "3.1"}
                    onChange={(event) =>
                      setFields((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                    required
                  >
                    <option value="3.1">3.1 — Brasil e Américas</option>
                    <option value="3.2">3.2 — Europa</option>
                    <option value="3.3">3.3 — Ásia-Pacífico</option>
                  </select>
                ) : (
                  <input
                    type={
                      field.key.toLowerCase().includes("secret")
                        ? "password"
                        : "text"
                    }
                    value={fields[field.key] ?? ""}
                    onChange={(event) =>
                      setFields((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                    autoComplete="off"
                    required
                  />
                )}
              </Field>
            ))}
            {selected.slug !== "amazon" ? (
              <p className="field__helper">
                Ao salvar, abra o conector instalado neste Mac uma única vez
                para concluir login/2FA. Depois ele reutiliza a sessão local.
              </p>
            ) : null}
            {message ? (
              <p className="success-message" role="status">
                {message}
              </p>
            ) : null}
          </form>
        ) : null}
      </Modal>
    </>
  );
}
